import { Injectable, Injector, computed, runInInjectionContext, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  Auth, signInWithEmailAndPassword, createUserWithEmailAndPassword,
  signOut, user, User, updateProfile,
  onAuthStateChanged, authState
} from '@angular/fire/auth';
import { Observable, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { AlertController } from '@ionic/angular/standalone';
import { ApiService } from './api.service';
import { NotificationService } from './notification.service';
import { UserDto } from 'src/app/models/myapp.model';
import { NEW_SIGN_IN_PAGE_URL } from 'src/app/myConstants/app.constants';

@Injectable({ providedIn: 'root' })
export class AuthService {

  user$: Observable<User | null>;

  readonly currentUser = signal<User | null>(null);
  readonly profileId = signal<string | null>(null);
  readonly cachedUserDto = signal<UserDto | null>(null);
  readonly unreadNotificationCount = signal<number>(0);

  readonly email = computed(() => this.currentUser()?.email ?? null);

  private registrationInProgress = false;
  get isRegistrationInProgress(): boolean { return this.registrationInProgress; }

  constructor(
    private auth: Auth,
    // private router: Router,
    private api: ApiService,
    private notificationService: NotificationService,
    private alertCtrl: AlertController,
    private injector: Injector
  ) {
    this.user$ = user(this.auth);
    onAuthStateChanged(this.auth, async firebaseUser => {
      const previousUser = this.currentUser();
      this.currentUser.set(firebaseUser);
      if (firebaseUser) {
        localStorage.removeItem('viewedPostIds');
        // Firebase stores the user profile and the ID token separately. After email
        // verification the stored user profile reflects emailVerified=true, but the
        // cached ID token (still within its 1-hour TTL) retains emailVerified=false.
        // Force-refresh the token so the cached JWT claims match before any API call.
        if (firebaseUser.emailVerified && !previousUser?.emailVerified) {
          try {
            await firebaseUser.getIdToken(true);
          } catch {
            // non-fatal — proceed with cached token
          }
        }
        this.api.getMe().pipe(take(1)).subscribe({
          next: dto => {
            this.profileId.set(dto.profileId);
            this.cachedUserDto.set(dto);
            if (dto.adminRestricted) {
              this.showRestrictedAlert();
            }
            void this.notificationService.syncTokenToBackend(this.api, firebaseUser.uid);
            this.api.getNotificationUnreadCount().pipe(take(1)).subscribe({
              next: res => this.unreadNotificationCount.set(res.count),
              error: () => {}
            });
          },
          error: (err) => {
            // console.log( ' authService err ', this.registrationInProgress, ' ',  err)
            if (!this.registrationInProgress && (err?.status === 404 || err?.status === 403)) {
              // console.log( ' authService logging out ')
              signOut(this.auth);
            }
          }
        });
      } else {
        this.profileId.set(null);
        this.cachedUserDto.set(null);
        this.unreadNotificationCount.set(0);
        if (previousUser) {
          localStorage.removeItem('viewedPostIds');
          window.location.href = NEW_SIGN_IN_PAGE_URL;
        }
      }
    });
  }

  /*
  async login(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(this.auth, email, password);
    // Sync user to backend on every login
    await this.api.syncMe().pipe(take(1)).toPromise();
  }

  async register(email: string, password: string, displayName: string): Promise<void> {
    this.registrationInProgress = true;
    try {
      const cred = await createUserWithEmailAndPassword(this.auth, email, password);
      if (displayName) {
        await updateProfile(cred.user, { displayName });
      }
      // 
      await this.api.syncMe().pipe(take(1)).toPromise();
    } finally {
      this.registrationInProgress = false;
    }
  }
  */

  async logout(): Promise<void> {
    await this.notificationService.clearTokenFromBackend(this.api);
    await signOut(this.auth);
    // why following was removed
    /*
    this.currentUser.set(null);
    this.profileId.set(null);
    this.cachedUserDto.set(null);
//    this.router.navigateByUrl('/auth/login');
    window.location.href = '/auth/login';
*/

  }

  /**
   * Get Firebase ID token. Returns null if not logged in.
   * Uses authState to wait for Firebase session restore before returning,
   * preventing race conditions on direct page navigation.
   */
  async getToken(): Promise<string | null> {
    const u = await firstValueFrom(runInInjectionContext(this.injector, () => authState(this.auth)));
    // If already signed in (e.g. just after signInWithEmailAndPassword), use currentUser
    // directly — authState fires asynchronously and may emit null first, causing a race.
    // Fall back to authState only when currentUser is null (session restore on page load).
    // const u = this.auth.currentUser ?? await firstValueFrom(authState(this.auth));
    if (!u) return null;
    try {
      return await Promise.race([
        u.getIdToken(),
        new Promise<string>((_, reject) =>
          setTimeout(() => reject(new Error('Token timeout')), 5000)
        )
      ]);
    } catch {
      return null;
    }
  }

  get isLoggedIn(): boolean {
    return !!this.auth.currentUser;
  }

  private async showRestrictedAlert(): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Account Restricted',
      message: 'Your account has been restricted by an administrator. Some actions are not available.',
      buttons: ['OK']
    });
    await alert.present();
  }
}
